import { createElement, useEffect, type ComponentType } from 'react';
import { can } from '@/lib/permissions';
import { api } from './client';
import { useMe } from './hooks';
import type { Permissions } from '@/lib/permissions';

/**
 * The signed-in user's role and permissions (from GET /api/me), mirrored to localStorage so the sidebar renders
 * filtered on first paint. Null only before the first ever load (everything is shown; the server still enforces).
 */
const KEY = 'cosmos:perms';

function cached(): Permissions | null {
  try { const raw = localStorage.getItem(KEY); return raw ? JSON.parse(raw) as Permissions : null; } catch { return null; }
}

export function usePermissions(): Permissions | null {
  const { data } = useMe();
  const perms = data?.permissions ?? null;
  useEffect(() => {
    if (perms) try { localStorage.setItem(KEY, JSON.stringify(perms)); } catch { /* storage unavailable */ }
  }, [perms]);
  return perms ?? cached();
}

/**
 * Start or stop "View as role". Reloads Cosmos at Home afterwards: every cached page and query belongs to the other
 * set of permissions, and the current page may not be open to the role.
 */
export async function setViewAs(roleId: string | null): Promise<void> {
  await api.put('/api/me/view-as', { roleId });
  try { localStorage.removeItem(KEY); } catch { /* storage unavailable */ }
  window.location.assign('/');
}

/**
 * A component that renders only when the user's role can read `area` — for cards that show another module's data
 * (the M365 card on a ticket, Huntress on a device…). The card gates itself, so every page that uses it is covered.
 */
export function withArea<P extends object>(area: string, Inner: ComponentType<P>): ComponentType<P> {
  const Gated = (props: P) => (can(usePermissions(), area) ? createElement(Inner, props) : null);
  Gated.displayName = `withArea(${area})`;
  return Gated;
}
